import React, { useEffect, useRef, useState } from "react";
import { motion as Motion, useInView } from "framer-motion";
import AnnouncementBanner from "../components/AnnouncementBanner";
import Button from "../components/Button";
import heroBgMain from "../assets/field-training.webp";
import heroBgMain640Webp from "../assets/hero-640.webp";
import heroBgMain1024Webp from "../assets/hero-1024.webp";
import heroBgMain1920Webp from "../assets/hero-1920.webp";
import heroBgMain640Avif from "../assets/hero-640.avif";
import heroBgMain1024Avif from "../assets/hero-1024.avif";
import heroBgMain1920Avif from "../assets/hero-1920.avif";
import heroSlide2 from "../assets/gallery/g2.webp";
import heroSlide2_640Webp from "../assets/hero-slide-2-640.webp";
import heroSlide2_1024Webp from "../assets/hero-slide-2-1024.webp";
import heroSlide2_1920Webp from "../assets/hero-slide-2-1920.webp";
import heroSlide2_640Avif from "../assets/hero-slide-2-640.avif";
import heroSlide2_1024Avif from "../assets/hero-slide-2-1024.avif";
import heroSlide2_1920Avif from "../assets/hero-slide-2-1920.avif";
import heroSlide3 from "../assets/gallery/g7.webp";
import heroSlide3_640Webp from "../assets/hero-slide-3-640.webp";
import heroSlide3_1024Webp from "../assets/hero-slide-3-1024.webp";
import heroSlide3_1920Webp from "../assets/hero-slide-3-1920.webp";
import heroSlide3_640Avif from "../assets/hero-slide-3-640.avif";
import heroSlide3_1024Avif from "../assets/hero-slide-3-1024.avif";
import heroSlide3_1920Avif from "../assets/hero-slide-3-1920.avif";

const Hero = () => {
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { amount: 0.1 });
  const [activeBackground, setActiveBackground] = useState(0);

  const slides = [
    {
      src: heroBgMain,
      alt: "Reflection Futbol Academy field training",
      focalClassName: "object-center",
      avif: [heroBgMain640Avif, heroBgMain1024Avif, heroBgMain1920Avif],
      webp: [heroBgMain640Webp, heroBgMain1024Webp, heroBgMain1920Webp],
    },
    {
      src: heroSlide2,
      alt: "Reflection Futbol Academy match and training moments",
      focalClassName: "object-top",
      avif: [heroSlide2_640Avif, heroSlide2_1024Avif, heroSlide2_1920Avif],
      webp: [heroSlide2_640Webp, heroSlide2_1024Webp, heroSlide2_1920Webp],
    },
    {
      src: heroSlide3,
      alt: "Reflection Futbol Academy academy culture and training",
      focalClassName: "object-top",
      avif: [heroSlide3_640Avif, heroSlide3_1024Avif, heroSlide3_1920Avif],
      webp: [heroSlide3_640Webp, heroSlide3_1024Webp, heroSlide3_1920Webp],
    },
  ];

  useEffect(() => {
    if (slides.length <= 1) return undefined;

    const interval = window.setInterval(() => {
      setActiveBackground((current) => (current + 1) % slides.length);
    }, 6500);

    return () => window.clearInterval(interval);
  }, [slides.length]);

  const container = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.12, delayChildren: 0.12 },
    },
  };

  const child = {
    hidden: { opacity: 0, y: 12 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: "easeOut" },
    },
  };

  return (
    <section
      ref={sectionRef}
      className="relative min-h-[100dvh] flex flex-col overflow-hidden bg-transparent"
    >
      <Motion.div
        aria-hidden="true"
        initial={{ opacity: 0 }}
        animate={{ opacity: isInView ? 1 : 0 }}
        transition={{ duration: 0.8 }}
        className="fixed inset-0 -z-10 bg-[rgba(0,0,0,0.5)]"
      />

      <div aria-hidden="true" className="absolute inset-0 -z-30">
        {slides.map((slide, index) => (
          <picture key={slide.src} className="absolute inset-0 block">
            <source
              type="image/avif"
              srcSet={`${slide.avif[0]} 640w, ${slide.avif[1]} 1024w, ${slide.avif[2]} 1920w`}
              sizes="100vw"
            />
            <source
              type="image/webp"
              srcSet={`${slide.webp[0]} 640w, ${slide.webp[1]} 1024w, ${slide.webp[2]} 1920w`}
              sizes="100vw"
            />
            <Motion.img
              src={slide.src}
              srcSet={`${slide.webp[0]} 640w, ${slide.webp[1]} 1024w, ${slide.webp[2]} 1920w`}
              sizes="100vw"
              alt={slide.alt}
              fetchPriority={index === 0 ? "high" : "auto"}
              width={1920}
              height={1080}
              className={`absolute inset-0 h-full w-full object-cover ${slide.focalClassName}`}
              initial={{ opacity: 0, scale: 1.04 }}
              animate={{
                opacity: isInView && activeBackground === index ? 1 : 0,
                scale: isInView && activeBackground === index ? 1 : 1.04,
              }}
              transition={{ duration: 1.2, ease: "easeOut" }}
            />
          </picture>
        ))}
      </div>

      {/* Main Content Spacer - Centers the content and pushes banner down */}
      <div className="flex-grow flex flex-col items-center justify-center pt-20">
        <Motion.div
          className="relative z-10 w-full max-w-5xl mx-auto site-gutter space-y-6 text-center pb-8"
          variants={container}
          initial="hidden"
          animate="visible"
        >
          <Motion.h1
            variants={child}
            className="hero-title text-white uppercase tracking-tight drop-shadow-2xl mt-4 md:mt-20"
          >
            <span className="block mb-2 md:mb-4 ">
              REFLECT YOUR <span className="text-[#F4B400]">TRUE</span>
            </span>
            <span className="block mb-2 md:mb-4">
              <span className="text-[#F4B400]">POTENTIAL</span> ON THE
            </span>
            <span className="block">PITCH</span>
          </Motion.h1>

          <Motion.p
            variants={child}
            className="hero-subtitle text-gray-100 font-inter max-w-3xl mx-auto drop-shadow-lg"
          >
            The premier academy for young athletes dedicated to achieving their
            professional football dreams through world-class training and
            discipline.
          </Motion.p>

          <Motion.div
            variants={child}
            className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center pt-6 items-center"
          >
            <div className="w-full max-w-[320px] sm:w-auto">
              <Button
                to="/apply"
                variant="primary"
                pill
                className="px-8 py-4 text-sm sm:text-base md:text-lg w-full ring-1 ring-[#F4B400]/15 hover:ring-[#F4B400]/25 shadow-soft"
              >
                JOIN THE ACADEMY
              </Button>
            </div>

            <div className="w-full max-w-[320px] sm:w-auto">
              <Button
                to="/interest-form"
                variant="outline"
                pill
                className="px-8 py-4 text-sm sm:text-base md:text-lg w-full ring-1 ring-white/20 hover:ring-white/30 shadow-soft"
              >
                INTEREST FORM
              </Button>
            </div>
          </Motion.div>
        </Motion.div>
      </div>

      <div className="w-full z-20 pb-[env(safe-area-inset-bottom)]">
        <AnnouncementBanner />
      </div>
    </section>
  );
};

export default Hero;
